import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge, Spacer, Table, Text,
  Tooltip,
} from '@oliasoft-open-source/react-ui-library';
import { sumBy } from 'es-toolkit';
import { createUnitCell, entityTableLabelMap, entityTypeMap } from 'common/table-viewer/utils';
import translations from 'client/internationalization/translation-map.json';
import { EntityType } from 'common/enums/entity-enums';
import { IObject } from 'common/interfaces/hierarchy-entities';
import { IQueryResultConfig, TQueryResultTable } from 'common/interfaces/query';
import { isVerticalTable } from 'common/utils/is-vertical-table';
import { useTranslation } from 'react-i18next';
import { to } from '@oliasoft-open-source/units';
import { getQueryResultExcel } from 'client/api-utils';
import { TableModal } from './table-modal';
import { filterQueryResult, getObjectValueRows, getResultEntityPath } from '../query-utils';

export type TQueryResultTableProps = {
  archiveId: string | undefined,
  queryResult: TQueryResultTable,
  resultConfig: IQueryResultConfig,
  setFilters: (data: { [key: string]: string }) => void,
};
export const QueryResultTable = ({
  archiveId, queryResult, resultConfig, setFilters,
}: TQueryResultTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId } = useParams();

  const { groupedDataType, data = [] } = queryResult || {};
  const { hierarchy = [], filters = {} } = resultConfig || {};

  const firstHeaderColumn = data?.[0]?.entityType || '';
  const [selectedData, setSelectedData] = useState<IObject | null>(null);
  const initialUnits = Object.entries(groupedDataType).map(([entityType, propNames]) => {
    const isVertical = isVerticalTable(entityType as EntityType);
    const mapObject = entityTypeMap.get(entityType as EntityType);
    return isVertical
      ? (propNames || []).map((propName) => mapObject?.get(propName)?.unit ?? '')
      : [''];
  });

  const [units, setUnits] = useState<string[][]>(initialUnits);
  const [dataRows, setDataRows] = useState<{ isVertical: boolean, entityType: string, dataType?: string, value: string | number }[][][]>([]);

  const filteredData = filterQueryResult(data, hierarchy, filters);
  useEffect(() => {
    const initialDataRows = filteredData?.map((result) => {
      const { objects } = result;

      return objects.map((object) => {
        const dataCells = [];
        for (const key in groupedDataType) {
          const isVertical = isVerticalTable(key as EntityType);
          const valueRows = getObjectValueRows(object?.[key]?.data);
          if (!isVertical) {
            dataCells.push({ entityType: key, isVertical, value: valueRows.length ? `View (${valueRows.length})` : '' });
          } else {
            groupedDataType[key].forEach((propName) => {
              dataCells.push({
                entityType: key, dataType: propName, isVertical, value: valueRows[0]?.[propName],
              });
            });
          }
        }

        return dataCells;
      });
    }) ?? [];
    setDataRows(prev => {
      if (JSON.stringify(prev) !== JSON.stringify(initialDataRows)) {
        return initialDataRows;
      }
      return prev;
    });
  }, [filteredData, groupedDataType]);

  const rows = filteredData?.flatMap((result, groupIdx) => {
    const { name, objects } = result;
    const groupRow = {
      value:
  <>
    <Text>{name}</Text>
    <Spacer width="var(--padding-xxs)" />
    <Tooltip text="Items per group"><Badge color="var(--color-neutral-400)" title={objects.length} /></Tooltip>
  </>,
      rowSpan: objects?.length,
    };

    return objects.map((object, rowIdx) => {
      const dataCells: { value: ReactElement; style?: { [key: string]: string } }[] = [];
      const hierarchyDataCells = hierarchy?.map((hierarchyItem: string) => ({
        value: Object.values(object)?.[0]?.metadata?.hierarchyData?.[hierarchyItem]?.name,
        style: { borderLeft: 'solid var(--color-border) 1px' },
      })) || [];

      const onCellClick = (entityType: string) => {
        const designId = object?.[entityType]?.metadata?.hierarchyData?.design?.id;
        navigate(getResultEntityPath(companyId, archiveId, designId));
      };
      dataRows?.[groupIdx]?.[rowIdx]?.forEach((item) => {
        if (!item.isVertical) {
          dataCells.push({
            value: <Text link onClick={() => setSelectedData(object?.[item.entityType])}>{item.value}</Text>,
          });
        } else {
          dataCells.push({
            value: <Text link onClick={() => onCellClick(item.entityType)}>{item.value}</Text>,
            ...(rowIdx > 0
              && { style: { borderLeft: 'solid var(--color-border) 1px' } }),
          });
        }
      });

      const cells = rowIdx === 0 ? [groupRow, ...hierarchyDataCells, ...dataCells] : [...hierarchyDataCells, ...dataCells];
      return { cells };
    });
  }) ?? [];
  const hierarchyHeaderCells = hierarchy?.map((hierarchyItem: string) => ({
    value: t(hierarchyItem),
  })) || [];

  const dataTypeFilterCells = Object.entries(groupedDataType).flatMap(([entityType, propNames]) => {
    const isVertical = isVerticalTable(entityType as EntityType);

    return isVertical ? (propNames || []).map((propName) => `${entityType}-${propName}`) : [''];
  });
  const dataFilterCells = [data[0]?.entityType, ...hierarchy, ...dataTypeFilterCells]?.map((key) => {
    const filterValue = filters?.[key] || '';
    const isDisabled = key === '';
    return {
      key,
      value: filterValue,
      type: 'Input',
      placeholder: isDisabled ? key : 'Filter',
      onChange: (ev: any) => setFilters({
        [key]: ev.target.value,
      }),
      disabled: isDisabled,
    };
  });

  const headers = [
    [{ value: t(firstHeaderColumn) },
      ...hierarchyHeaderCells,
      ...Object.entries(groupedDataType).map(([entityType, propNames]) => ({
        value: entityTableLabelMap[entityType as EntityType] || entityType,
        colSpan: isVerticalTable(entityType as EntityType) ? propNames.length : 1,
      }))],
    [
      { value: '' },
      ...Object.entries(groupedDataType).flatMap(([entityType, propNames]) => {
        const isVertical = isVerticalTable(entityType as EntityType);
        const mapObject = entityTypeMap.get(entityType as EntityType);

        return isVertical ? (propNames || []).map((propName) => ({
          value: mapObject?.get(propName)?.label,
        })) : [{ value: '' }];
      }),
    ],
  ];

  const actions = [
    {
      icon: 'download',
      label: `${t(translations.importExport_download)} EXCEL`,
      onClick: () => {
        getQueryResultExcel({
          headers, units, dataRows, queryResult,
        });
      },
    }];

  const table = {
    name: '',
    headers: [
      // entity labels and hierarchy items row
      {
        cells: [
          {
            value:
  <>
    <Text>{t(firstHeaderColumn)}</Text>
    <Spacer width="var(--padding-xxs)" />
    <Tooltip text="Total Groups"><Badge color="var(--color-neutral-400)" title={filteredData.length} /></Tooltip>
    <Spacer width="var(--padding-xxs)" />
    <Tooltip text="Total Items"><Badge color="var(--color-neutral-400)" title={sumBy(filteredData, (obj) => obj.objects.length)} /></Tooltip>
  </>,
          },
          ...hierarchyHeaderCells,
          ...Object.entries(groupedDataType).map(([entityType, propNames]) => ({
            value: entityTableLabelMap[entityType as EntityType] || entityType,
            colSpan: isVerticalTable(entityType as EntityType) ? propNames.length : 1,
          })),
        ],
      },
      // entity parameteres row (prop names)
      {
        cells: [
          {},
          ...hierarchyHeaderCells.map(() => ({})),
          ...Object.entries(groupedDataType).flatMap(([entityType, propNames]) => {
            const isVertical = isVerticalTable(entityType as EntityType);
            const mapObject = entityTypeMap.get(entityType as EntityType);

            return isVertical ? (propNames || []).map((propName) => ({
              value: mapObject?.get(propName)?.label,
            })) : {};
          }),
        ],
      },
      // unit row
      {
        cells: [
          {},
          ...hierarchyHeaderCells.map(() => ({})),
          ...Object.entries(groupedDataType).flatMap(([entityType, propNames], groupIdx) => {
            const isVertical = isVerticalTable(entityType as EntityType);
            const mapObject = entityTypeMap.get(entityType as EntityType);
            return isVertical ? (propNames || []).map((propName, itemIdx) => (
              createUnitCell(units[groupIdx][itemIdx], mapObject?.get(propName)?.quantity, (e) => {
                const oldUnit = units[groupIdx][itemIdx];
                const newUnit = e;
                const convertedDataRows = dataRows.map((row) => {
                  return row.map(cells => {
                    const itemIdx = cells.findIndex(c => c.entityType === entityType && c.dataType === propName);
                    if (itemIdx === -1) return cells;
                    cells[itemIdx] = {
                      ...cells[itemIdx],
                      value: cells[itemIdx].value ? to(cells[itemIdx].value, oldUnit, newUnit) : '',
                    };
                    return cells;
                  });
                });
                setDataRows(convertedDataRows);
                setUnits(prevUnits => {
                  const newUnits = [...prevUnits];
                  newUnits[groupIdx][itemIdx] = e;
                  return newUnits;
                });
              })
            )) : {};
          }),
        ],
      },
      // filter row
      {
        cells: dataFilterCells,
      },
    ],
    rows,
    actions,
    stickyHeaders: 'true',
  };

  return (
    <>
      <Table table={table} />
      {selectedData && <TableModal data={selectedData} onClose={() => setSelectedData(null)} visibleColumns={groupedDataType[selectedData.type ?? '']} />}
    </>
  );
};
